import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from './AdminLayout';
import RequireAdmin from './RequireAdmin';
import { apiUrl, fetchJson } from '../apiBase';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Switch from '@mui/material/Switch';
import IconButton from '@mui/material/IconButton';
import Collapse from '@mui/material/Collapse';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import BugReport from '@mui/icons-material/BugReport';

async function jsonFetch(path, opts = {}) {
  const isAbsolute = /^https?:/i.test(path);
  const url = isAbsolute ? path : apiUrl(path);
  const res = await fetchJson(url, {
    method: opts.method || 'GET',
    headers: { 'Content-Type': 'application/json', ...(opts.headers || {}) },
    body: opts.body,
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

// Internal shared content so we can embed inside Dashboard without AdminLayout
export function PlaygroundContent({ embedded = false }) {
  const [schoolId, setSchoolId] = useState('');
  const [parentPhone, setParentPhone] = useState('');
  const [freezeTime, setFreezeTime] = useState(true);
  const [sessionId, setSessionId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [turns, setTurns] = useState([]); // {msg, reply, intent, template, ms, raw}
  const [stats, setStats] = useState(null);
  const [autoStats, setAutoStats] = useState(false);
  const [error, setError] = useState(null);
  const [ending, setEnding] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [showDebug, setShowDebug] = useState(false);
  const [showStats, setShowStats] = useState(true);

  const intentColor = useCallback((intent) => {
    if (!intent) return 'default';
    switch (intent) {
      case 'absence_report': return 'error';
      case 'absence_cancel': return 'success';
      case 'attendance_status': return 'info';
      default: return 'default';
    }
  }, []);

  const lastTurn = turns.length ? turns[turns.length - 1] : null;

  const resetSession = useCallback(async () => {
    if (!sessionId) return;
    setResetting(true);
    try {
  await jsonFetch('/admin/playground/reset', { method: 'POST', body: JSON.stringify({ session_id: sessionId }) });
      setTurns([]); setStats(null);
    } catch (e) {
      setError(`Reset failed: ${e.message}`);
    } finally { setResetting(false); }
  }, [sessionId]);

  const endSession = useCallback(async () => {
    if (!sessionId) return;
    setEnding(true);
    try {
  await jsonFetch('/admin/playground/end', { method: 'POST', body: JSON.stringify({ session_id: sessionId }) });
      setSessionId(null); setTurns([]); setStats(null);
    } catch (e) {
      setError(`End failed: ${e.message}`);
    } finally { setEnding(false); }
  }, [sessionId]);

  const createSession = useCallback(async () => {
    setCreating(true); setError(null); setTurns([]); setStats(null);
    try {
      const body = { school_id: schoolId, parent_phone: parentPhone, freeze_time: freezeTime };
  const data = await jsonFetch('/admin/playground/session', { method: 'POST', body: JSON.stringify(body) });
      setSessionId(data.session_id);
    } catch (e) {
      setError(`Session create failed: ${e.message}`);
    } finally { setCreating(false); }
  }, [schoolId, parentPhone, freezeTime]);

  const sendMessage = useCallback(async () => {
    if (!sessionId || !message.trim()) return;
    setSending(true); setError(null);
    try {
      const body = { session_id: sessionId, message };
      const data = await jsonFetch('/admin/playground/simulate', { method: 'POST', body: JSON.stringify(body) });
      setTurns(ts => [...ts, { msg: message, reply: data.rendered, intent: data.turn?.intent, template: data.turn?.template_key, ms: data.elapsed_ms, raw: data }]);
      setMessage('');
    } catch (e) {
      setError(`Send failed: ${e.message}`);
    } finally { setSending(false); }
  }, [sessionId, message]);

  const fetchStats = useCallback(async () => {
    if (!sessionId) return;
    try {
  const s = await jsonFetch(`/admin/playground/overlay/${sessionId}/attendance/stats`);
      setStats(s);
    } catch (e) {
      // ignore transient
    }
  }, [sessionId]);

  // This is a diagnostic playground, not a real-time classroom dashboard.
  // Auto stats are opt-in and never poll faster than once per minute.
  useEffect(() => {
    if (!autoStats || !sessionId) return undefined;
    fetchStats();
    const id = window.setInterval(fetchStats, 60000);
    return () => window.clearInterval(id);
  }, [autoStats, sessionId, fetchStats]);

  const content = (
        <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', p: 0, bgcolor: '#F9FAFB' }}>
          <Box sx={{ flex: 2, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>Oturum</Typography>
                {sessionId && <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{sessionId}</Typography>}
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
                <TextField size="small" label="Okul ID" value={schoolId} onChange={e => setSchoolId(e.target.value)} sx={{ minWidth: 140 }} />
                <TextField size="small" label="Veli Telefon" value={parentPhone} onChange={e => setParentPhone(e.target.value)} sx={{ minWidth: 180 }} />
                <FormControlLabel control={<Checkbox checked={freezeTime} onChange={e => setFreezeTime(e.target.checked)} />} label="Zamanı Dondur" />
              </Stack>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Button variant="contained" onClick={createSession} disabled={creating} size="small">{creating ? 'Oluşturuluyor...' : 'Oturum Oluştur'}</Button>
                {sessionId && <Button variant="outlined" color="warning" onClick={resetSession} disabled={resetting} size="small">{resetting ? 'Sıfırlanıyor...' : 'Sıfırla'}</Button>}
                {sessionId && <Button variant="outlined" color="error" onClick={endSession} disabled={ending} size="small">{ending ? 'Bitiriliyor...' : 'Bitir'}</Button>}
                <FormControlLabel control={<Switch checked={autoStats} onChange={e => setAutoStats(e.target.checked)} size="small" />} label="Oto İstatistik" />
                <FormControlLabel control={<Switch checked={showDebug} onChange={e => setShowDebug(e.target.checked)} size="small" />} label="Debug" />
              </Stack>
            </Paper>

            <Paper elevation={1} sx={{ p: 2, borderRadius: 2, display: 'flex', flexDirection: 'column', height: 520 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>Mesajlar</Typography>
              <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                <TextField
                  size="small"
                  fullWidth
                  disabled={!sessionId}
                  placeholder="Mesaj yaz..."
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') sendMessage(); }}
                />
                <Button variant="contained" onClick={sendMessage} disabled={!sessionId || sending}>{sending ? 'Gönderiliyor...' : 'Gönder'}</Button>
              </Stack>
              {error && <Typography variant="body2" color="error" sx={{ mb: 1 }}>{error}</Typography>}
              <Box sx={{ flex: 1, overflow: 'auto', pr: 1 }}>
                {turns.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    {sessionId ? 'Henüz mesaj yok.' : 'Önce bir oturum oluşturun.'}
                  </Typography>
                )}
                <Stack spacing={1.5}>
                  {turns.map((t, i) => (
                    <Paper key={i} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Siz</Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-line', mb: 1 }}>{t.msg}</Typography>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Asistan</Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-line', mb: 1 }} dangerouslySetInnerHTML={{ __html: t.reply?.replace(/\n/g, '<br/>') }} />
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Chip label={t.intent || 'unknown'} size="small" color={intentColor(t.intent)} variant={t.intent ? 'filled' : 'outlined'} />
                        {t.template && <Chip label={t.template} size="small" color="secondary" variant="outlined" />}
                        <Chip label={`${t.ms}ms`} size="small" variant="outlined" />
                      </Stack>
                      {showDebug && t.raw && (
                        <Collapse in={showDebug} timeout="auto">
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="caption" sx={{ fontWeight: 600 }}>Ham JSON</Typography>
                            <Box component="pre" sx={{ bgcolor: '#FAFAFA', p: 1, mt: 0.5, borderRadius: 1, maxHeight: 200, overflow: 'auto', fontSize: 11 }}>
                              {JSON.stringify(t.raw.turn, null, 2)}
                            </Box>
                          </Box>
                        </Collapse>
                      )}
                    </Paper>
                  ))}
                </Stack>
              </Box>
            </Paper>
          </Box>

          <Box sx={{ flex: 1, minWidth: 300, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>Overlay İstatistik</Typography>
                <IconButton size="small" onClick={() => setShowStats(s => !s)}>{showStats ? <ExpandLess /> : <ExpandMore />}</IconButton>
              </Stack>
              <Collapse in={showStats} timeout="auto" unmountOnExit={false}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <Button size="small" variant="outlined" disabled={!sessionId} onClick={fetchStats}>Yenile</Button>
                </Stack>
                {stats ? (
                  <Box component="pre" sx={{ fontSize: 12, bgcolor: '#FAFAFA', p: 1.5, borderRadius: 1, maxHeight: 300, overflow: 'auto', mb: 1 }}>
                    {JSON.stringify(stats, null, 2)}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">{sessionId ? 'Henüz istatistik yok.' : 'Oturum oluşturun.'}</Typography>
                )}
              </Collapse>
            </Paper>

            {showDebug && lastTurn && (
              <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                  <BugReport fontSize="small" />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>Son Turn Debug</Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary">Tüm yanıt nesnesi</Typography>
                <Box component="pre" sx={{ fontSize: 11, bgcolor: '#FAFAFA', p: 1, borderRadius: 1, maxHeight: 260, overflow: 'auto' }}>
                  {JSON.stringify(lastTurn.raw, null, 2)}
                </Box>
              </Paper>
            )}
          </Box>
        </Box>
  );

  if (embedded) return content;
  return (
    <RequireAdmin>
      <AdminLayout title="Simülasyon Alanı">
        {content}
      </AdminLayout>
    </RequireAdmin>
  );
}

export default function PlaygroundPanel() {
  return <PlaygroundContent embedded={false} />;
}
